import { Box, Stack, Typography as MuiTypography, useTheme } from "@mui/material";
import { Typography as FoodsTypography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { JsonAttributeField, type JsonAttributeFormValue } from "src/components/JsonAttributes";
import { IngredientFormType } from "../../../IngredientForm.types";
import type { ComponentAppearance } from "src/components/appearance";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

type IngredientJsonAttributesFieldProps = {
    ingredient: IngredientFormType;
    setIngredient: (updater: (prev: IngredientFormType) => IngredientFormType) => void;
    appearance?: ComponentAppearance;
};

export const IngredientJsonAttributesField = ({
    ingredient,
    setIngredient,
    appearance = "legacy",
}: IngredientJsonAttributesFieldProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isProductUxV2 = appearance === "product-v2";
    const { entitiesJsonAttributes } = useFoodpilotContext();

    const ingredientAttributes = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.ingredient] || [];

    if (ingredientAttributes.length === 0) {
        return null;
    }

    const handleAttributeChange = (identifier: string, value: JsonAttributeFormValue) => {
        setIngredient((prevIngredient) => ({
            ...prevIngredient,
            jsonAttributes: {
                ...prevIngredient.jsonAttributes,
                [identifier]: value,
            },
        }));
    };

    return (
        <Box>
            {isProductUxV2 ?
                <FoodsTypography variant="h4" sx={{ marginBottom: theme.spacing(3) }}>
                    {t("ingredient.jsonAttributes.title", "Additional Information")}
                </FoodsTypography>
            :   <MuiTypography variant="h4" sx={{ marginBottom: theme.spacing(3) }}>
                    {t("ingredient.jsonAttributes.title", "Additional Information")}
                </MuiTypography>
            }
            <Stack spacing={3}>
                {ingredientAttributes.map((attribute) => (
                    <Stack key={attribute.identifier} maxWidth="70%">
                        <JsonAttributeField
                            attribute={attribute}
                            value={
                                ingredient.jsonAttributes?.[attribute.identifier] as JsonAttributeFormValue | undefined
                            }
                            onChange={(value) => handleAttributeChange(attribute.identifier, value)}
                            labelVariant="h5"
                            appearance={appearance}
                        />
                    </Stack>
                ))}
            </Stack>
        </Box>
    );
};
